<template>
  <div>
    <div class="spotlight-image-container relative" v-if="!images || images.length <= 1">
      <SpotlightImagesArray
        :image="images[0]!.image"
        :mobileImage="images[0]!.mobileImage"
        :loading="index < 2 ? 'eager' : 'lazy'"
        :fetchpriority="index < 2 ? 'high' : 'auto'"
      />
    </div>
    <div class="relative" v-else>
      <div class="spotlight-image-container relative"></div>
      <div
        :class="{
          'opacity-100': !isSwiperReady,
          'opacity-0 pointer-events-none': isSwiperReady,
        }"
        class="spotlight-image-container absolute top-0 left-0 transition-opacity duration-[2000ms] z-20"
      >
        <SpotlightImagesArray
          :image="images[0]!.image"
          :mobileImage="images[0]!.mobileImage"
          :loading="index < 2 ? 'eager' : 'lazy'"
          :fetchpriority="index < 2 ? 'high' : 'auto'"
        />
      </div>
      <ClientOnly>
        <swiper-container
          @swiperinit="swiperInitialized = true"
          effect="slide"
          autoplay-delay="5000"
          autoplay-disable-on-interaction="false"
          autoplay-reverse-direction="false"
          :loop="images.length > 3"
          :pagination="{
            el: swiperPagination,
            clickable: false,
          }"
          ref="swiperContainer"
          class="spotlight-swiper spotlight-image-container absolute top-0 left-0 w-full z-10"
          :class="{
            'opacity-0 pointer-events-none': !isSwiperReady,
            'opacity-100 swiper-initialized': isSwiperReady,
          }"
        >
          <swiper-slide
            v-for="(image, i) in images"
            :key="image.image.id"
            :lazy="i !== 0"
            class="!w-full overflow-hidden"
          >
            <SpotlightImagesArray
              :image="image.image"
              :mobileImage="image.mobileImage"
              :loading="i < 2 ? 'eager' : 'lazy'"
              :fetchpriority="i < 2 ? 'high' : 'auto'"
              :imageClass="`${i === 0 ? 'spotlight-image__img--not-animate' : 'spotlight-image__img--animate'}`"
              @load="imageLoaded(i)"
            />
          </swiper-slide>
        </swiper-container>
      </ClientOnly>
      <div class="flex justify-between items-center px-4 pt-2 md:pt-4 pb-2">
        <div class="swiper-pagination w-max" ref="swiperPagination"></div>
        <div class="buttons flex gap-2 w-max">
          <SharedIconButton
            variant="accent"
            @click="swiper.prev()"
            icon="arrow-left"
            class="w-10 h-10"
            :aria-label="t('swiper.previous')"
            :title="t('swiper.previous')"
            ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12.7069 17.293L8.41394 13H17.9999V11H8.41394L12.7069 6.70697L11.2929 5.29297L4.58594 12L11.2929 18.707L12.7069 17.293Z"
                fill="currentColor"
              />
            </svg>
          </SharedIconButton>
          <SharedIconButton
            variant="accent"
            @click="swiper.next()"
            icon="arrow-right"
            class="w-10 h-10"
            :aria-label="t('swiper.next')"
            :title="t('swiper.next')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M11.2931 17.293L15.5861 13H6.00006V11H15.5861L11.2931 6.70697L12.7071 5.29297L19.4141 12L12.7071 18.707L11.2931 17.293Z"
                fill="currentColor"
              />
            </svg>
          </SharedIconButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { Image } from '~/types/strapi';
  import type { SwiperContainer } from 'swiper/element';

  import SpotlightImagesArray from './SpotlightImagesArray.vue';

  const { t } = useI18n();

  const props = defineProps<{
    images: Image[];
    index: number;
  }>();

  const swiperContainer = ref<SwiperContainer | null>(null);
  const swiperPagination = ref<HTMLElement | null>(null);
  const swiper = useSwiper(swiperContainer, {
    loop: props.images.length > 3,
    slidesPerView: 1,
    pagination: {
      el: swiperPagination.value,
      clickable: false,
    },
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
  });

  const swiperInitialized = ref(false);
  const firstSlideLoaded = ref(false);
  const isSwiperReady = computed(() => swiperInitialized.value && firstSlideLoaded.value);

  const imageLoaded = (index: number) => {
    if (index === 0) {
      firstSlideLoaded.value = true;
    }
  };

  watch(firstSlideLoaded, value => {
    // start off things once the first slide is loaded properly
    if (value) {
      nextTick(() => {
        if (swiper.instance.value) {
          swiper.instance.value.autoplay.start();

          swiperContainer.value?.querySelectorAll('.spotlight-image__img--not-animate').forEach(el => {
            el.classList.remove('spotlight-image__img--not-animate');
            el.classList.add('spotlight-image__img--animate');
          });
        }
      });
    }
  });
</script>

<style lang="scss" scoped>
  .spotlight-swiper {
    &::part(pagination) {
      @apply hidden;
      @apply pointer-events-none;

      @screen md {
        @apply block w-max;
        @apply left-6;
      }

      @screen xl {
        @apply left-1/2;
        @apply -translate-x-[610px] -translate-y-[10px];
      }
    }
    &::part(bullet) {
      @apply pointer-events-none;
      @apply bg-white opacity-20;
    }
    &::part(bullet-active) {
      @apply bg-white bg-opacity-100;
    }
  }

  .swiper-pagination {
    @apply w-max flex gap-2;
    :deep(.swiper-pagination-bullet) {
      @apply bg-accent/20 block w-2 h-2 rounded-full;
    }
    :deep(.swiper-pagination-bullet-active) {
      @apply bg-accent;
    }
  }

  .spotlight-image-container {
    @apply w-full h-[200px];
    @screen md {
      @apply h-[360px];
    }
    @screen lg {
      @apply h-[1000px];
    }
    @screen xl {
      @apply h-[900px];
    }

    @screen 2xl {
      @apply h-[800px];
    }
  }
</style>

<style lang="scss">
  .spotlight-swiper {
    .spotlight-image {
      &__img {
        transition: transform 12s ease-out;
        transform: scale(1);
      }
    }

    // CSS-only approach for active slide zoom
    &.swiper-initialized {
      .swiper-slide-active {
        .spotlight-image__img--animate {
          transform: scale(1.05);
          transition: transform 12s ease-out;
        }
      }
    }
  }
</style>
